<template>
  <v-dialog v-model="dialogVisible" max-width="460px" :persistent="loading">
    <v-card class="cl-dialog-card">
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text v-if="sent">
        <p class="report-lede" role="status">
          Thanks for telling us. Your report has gone to the cloudloop team.
        </p>
        <p v-if="targetType === 'user'" class="report-note">
          You can also block {{ targetLabel || targetId }} from the menu on
          their profile.
        </p>
      </v-card-text>

      <v-card-text v-else>
        <p class="report-lede">
          What's wrong? Your report goes to the cloudloop team, not to the
          person you're reporting.
        </p>
        <v-radio-group
          v-model="reason"
          class="report-reasons"
          density="compact"
          hide-details
          :disabled="loading"
        >
          <v-radio
            v-for="r in reasons"
            :key="r.value"
            :label="r.label"
            :value="r.value"
          />
        </v-radio-group>
        <v-textarea
          v-model="details"
          class="report-details"
          label="Anything else we should know? (optional)"
          variant="outlined"
          density="compact"
          rows="3"
          auto-grow
          :counter="detailsMax"
          :maxlength="detailsMax"
          :disabled="loading"
        />
        <p v-if="error" class="report-error" role="alert">{{ error }}</p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="sent">
          <v-btn
            class="cl-btn cl-btn--primary"
            variant="outlined"
            @click="dialogVisible = false"
          >
            Done
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            class="cl-btn cl-btn--ghost"
            variant="text"
            :disabled="loading"
            @click="dialogVisible = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="cl-btn cl-btn--primary"
            variant="outlined"
            :loading="loading"
            :disabled="!reason"
            @click="send()"
          >
            Send report
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import cloudloop from "@/mixins/cloudloop";
import {
  REPORT_DETAILS_MAX,
  REPORT_REASONS,
  buildReportPayload,
  reportErrorMessage,
} from "@/services/safety";

export default {
  name: "ReportDialog",
  mixins: [cloudloop],
  props: {
    show: Boolean,
    // 'session' | 'loop' | 'user'
    targetType: { type: String, required: true },
    // Session name, loop hash or username.
    targetId: { type: String, required: true },
    // Optional friendlier name for the title (e.g. a session's display name).
    targetLabel: { type: String, default: "" },
  },
  emits: ["update:show", "reported"],
  data() {
    return {
      reasons: REPORT_REASONS,
      detailsMax: REPORT_DETAILS_MAX,
      reason: null,
      details: "",
      loading: false,
      error: "",
      sent: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    title() {
      if (this.sent) return "Report sent";
      const name = this.targetLabel || this.targetId;
      switch (this.targetType) {
        case "user":
          return `Report ${name}`;
        case "session":
          return "Report this session";
        default:
          return "Report this loop";
      }
    },
  },
  watch: {
    show(open) {
      if (!open) return;
      this.reason = null;
      this.details = "";
      this.error = "";
      this.sent = false;
    },
  },
  methods: {
    async send() {
      if (this.loading) return;
      let payload;
      try {
        payload = buildReportPayload({
          targetType: this.targetType,
          targetId: this.targetId,
          reason: this.reason,
          details: this.details,
        });
      } catch (e) {
        this.error = e.message.charAt(0).toUpperCase() + e.message.slice(1) + ".";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const res = await this.reportContent(payload);
        this.sent = true;
        this.$emit("reported", res);
      } catch (e) {
        this.error = reportErrorMessage(e && e.status, this.targetType);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.report-lede {
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin: 0 0 0.5rem;
}

.report-note {
  font-size: var(--font-sm);
  color: var(--text-muted);
  margin: 0.5rem 0 0;
}

.report-reasons {
  margin: 0 0 0.75rem;
}

.report-details {
  margin-top: 0.25rem;
}

.report-error {
  font-size: var(--font-sm);
  color: var(--danger);
  margin: 0.5rem 0 0;
}
</style>
